import { twMerge } from "tailwind-merge";
import { NextResponse, NextRequest } from "next/server";
import { getToken } from "next-auth/jwt";
import { clsx, type ClassValue } from "clsx";
import * as XLSX from "xlsx";

export const branchDisplayMap: Record<string, string> = {
  "SPM BHIWANDI": "APML PKG CENTER BHIWANDI (SPM)",
  "APML PKG CENTER BHIWANDI": "APML PKG CENTER BHIWANDI (SPM)",
  "APML PKG CENTER PUNE": "APML PKG CENTER PUNE (SPM PUNE)",
  "APML PUNE": "APML PKG CENTER PUNE (SPM PUNE)",
  "SPM PUNE": "APML PKG CENTER PUNE (SPM PUNE)",
  "APML BHIWANDI": "APML BHIWANDI"
};

export function getBranchDisplayName(branch: string | undefined): string {
  if (!branch) return "";
  // Normalize whitespace: uppercase, trim, and collapse multiple spaces
  const normalizedBranch = branch.toUpperCase().trim().replace(/\s+/g, " ");
  const displayName = branchDisplayMap[normalizedBranch] || branch;
  
  // Remove (WCPO) and (WOS)
  return displayName.replace(/\(WCPO\)|\(WOS\)/gi, "").trim();
}


export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function timePassed(date: Date | string) {
  const currentTime = new Date();
  const givenTime = new Date(date);

  const diffMs = currentTime.getTime() - givenTime.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  return `${diffHours}h ${diffMinutes}m`;
}

export function getTimePassed(dateString1: string, dateString2?: string) {
  const date1 = new Date(dateString1);
  const date2 = dateString2 ? new Date(dateString2) : new Date();
  const timeDiff = date1.getTime() - date2.getTime();

  const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;

  if (isNaN(seconds)) return "";

  return `${seconds}s`;
}

export function calculateDynamicTatStatus(
  status: string,
  targetDateStr?: string,
  deliveryDateStr?: string | null,
  dbTatStatus?: string,
  dbDeliveryStatus?: string
): string {
  let delayInfo = null;
  
  if (targetDateStr && (status === "COMPLETED" || status === "INTRANSIT")) {
    const target = new Date(targetDateStr);
    const actual = deliveryDateStr ? new Date(deliveryDateStr) : new Date();
    
    if (!isNaN(target.getTime()) && !isNaN(actual.getTime())) {
      const diffMs = actual.getTime() - target.getTime();
      if (diffMs > 0) {
        const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        delayInfo = { days, hours, isDelayed: true };
      }
    }
  }

  if (delayInfo) {
    if (delayInfo.days >= 3) return "Super Delayed";
    if (delayInfo.days >= 1) return "Delayed";
    return "Late";
  }

  if (status === "INTRANSIT") {
    switch (dbTatStatus) {
      case "onTime": return "On Time";
      case "late": return "Late";
      case "delayed": return "Delayed";
      case "superDelayed": return "Super Delayed";
      case "na": return "N/A";
      default: return "On Time";
    }
  }

  if (status === "COMPLETED") {
    const finalLabel = dbDeliveryStatus || dbTatStatus;
    switch (finalLabel) {
      case "onTime":
      case "early": return "On Time";
      case "delayed": return "Delayed";
      case "superDelayed": return "Super Delayed";
      default: return "On Time";
    }
  }

  return dbTatStatus || "N/A";
}

export async function generateExcel(
  data: any[],
  fileName: string = "ShipmentData.xlsx"
) {
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(
    data.flat().map((item, index) => {
      // Get tracking data for various statuses
      const trackingData = item.tracking || [];
      const firstTracking = trackingData[0];
      const lastTracking = trackingData[trackingData.length - 1];

      // Find the first departure to determine division
      const firstDeparture = trackingData.find(
        (t: any) => t.status === "departed"
      );
      const division = firstDeparture?.origin || item.from?.name || "-";

      // Find specific tracking events
      const bhiwandiToPune = trackingData.find(
        (t: any) =>
          t.status === "departed" &&
          t.origin?.toLowerCase().includes("bhiwandi") &&
          t.destination?.toLowerCase().includes("pune")
      );
      const puneToBhiwandi = trackingData.find(
        (t: any) =>
          t.status === "departed" &&
          t.origin?.toLowerCase().includes("pune") &&
          t.destination?.toLowerCase().includes("bhiwandi")
      );
      const chennaiDispatch = trackingData.find(
        (t: any) =>
          t.status === "departed" &&
          t.destination?.toLowerCase().includes("chennai")
      );

      // Find arrival events
      const puneArrival = trackingData.find(
        (t: any) =>
          t.status === "arrived" &&
          t.destination?.toLowerCase().includes("pune")
      );
      const bhiwandiArrival = trackingData.find(
        (t: any) =>
          t.status === "arrived" &&
          t.destination?.toLowerCase().includes("bhiwandi")
      );

      // Format dates to DD/MM/YYYY
      const formatDate = (date: string | Date) => {
        if (!date) return "-";
        return new Date(date).toLocaleDateString("en-GB");
      };

      // Format time to HH:MM
      const formatTime = (date: string | Date) => {
        if (!date) return "-";
        return new Date(date).toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        });
      };

      // Get number of articles from orderProduct and noArticles
      const getNoOfArticles = () => {
        if (item.noArticles) {
          return item.noArticles.toString();
        } else if (item.orderProduct) {
          // Try to extract number from orderProduct if it contains a number
          const match = item.orderProduct.match(/\d+/);
          return match ? match[0] : "-";
        }
        return "-";
      };

      return {
        "SR.NO": index + 1,
        "Booking Date": formatDate(item.createdAt),
        Division: division,
        "GC Number": item.gcNumber || "-",
        "Invoice Number": item.invoiceNumber || "-",
        "Consignee Name": item.to?.name || "-",
        "Transport Name": "APML",
        "From Location": item.from?.name || "-",
        "To Location": item.to?.name || "-",
        "No. of Articles": getNoOfArticles(),
        "Actual Weight": item.actualWeight || "-",
        "Charged Weight": item.chargedWeight || "-",
        "Vehicle Number":
          item.vehicleNumber?.[item.vehicleNumber.length - 1]?.number || "-",
        "Dispatch Date (Bhiwandi W/H to Pune W/H)": formatDate(
          bhiwandiToPune?.time
        ),
        "Dispatch Time (Bhiwandi SPM Hub)": formatTime(bhiwandiToPune?.time),
        "Receiving Date (Pune Packaging)": formatDate(puneArrival?.time),
        "Reporting Time (Pune SPM Hub)": formatTime(puneArrival?.time),
        "Dispatch Date (Pune W/H to Bhiwandi)": formatDate(
          puneToBhiwandi?.time
        ),
        "Dispatch Time (Pune SPM Hub)": formatTime(puneToBhiwandi?.time),
        "Receiving Date (Bhiwandi Packaging)": formatDate(
          bhiwandiArrival?.time
        ),
        "Reporting Time (Bhiwandi SPM Hub)": formatTime(bhiwandiArrival?.time),
        "Dispatch Date (Chennai/NCR)": formatDate(chennaiDispatch?.time),
        "Dispatch Time (Chennai/NCR)": formatTime(chennaiDispatch?.time),
        "Material Status": lastTracking
          ? `${lastTracking.status} ${
              lastTracking.destination ? "to " + lastTracking.destination : ""
            }`
          : "-",
        "Last Mile Vehicle Number": "-",
        "Delivery Date": formatDate(item.deliveryDate),
        "EDD": formatDate(item.targetDate),
        "Tat Status": calculateDynamicTatStatus(item.status, item.targetDate, item.deliveryDate, item.tatStatus, item.deliveryStatus),
        Remarks: item.remarks?.[0]?.remark || "-",
        "E-way Bill Number": item.ewayBillNumber || "-",
      };
    })
  );

  // Set column widths
  const columnWidths = [
    { wch: 5 }, // SR.NO
    { wch: 12 }, // Booking Date
    { wch: 15 }, // Division
    { wch: 15 }, // GC Number
    { wch: 15 }, // Invoice Number
    { wch: 20 }, // Consignee Name
    { wch: 15 }, // Transport Name
    { wch: 15 }, // From Location
    { wch: 15 }, // To Location
    { wch: 12 }, // No. of Articles
    { wch: 12 }, // Actual Weight
    { wch: 12 }, // Charged Weight
    { wch: 15 }, // Vehicle Number
    { wch: 20 }, // Dispatch Date (Bhiwandi W/H to Pune W/H)
    { wch: 20 }, // Dispatch Time (Bhiwandi SPM Hub)
    { wch: 20 }, // Receiving Date (Pune Packaging)
    { wch: 20 }, // Reporting Time (Pune SPM Hub)
    { wch: 20 }, // Dispatch Date (Pune W/H to Bhiwandi)
    { wch: 20 }, // Dispatch Time (Pune SPM Hub)
    { wch: 20 }, // Receiving Date (Bhiwandi Packaging)
    { wch: 20 }, // Reporting Time (Bhiwandi SPM Hub)
    { wch: 20 }, // Dispatch Date (Chennai/NCR)
    { wch: 20 }, // Dispatch Time (Chennai/NCR)
    { wch: 20 }, // Material Status
    { wch: 20 }, // Last Mile Vehicle Number
    { wch: 12 }, // Delivery Date
    { wch: 15 }, // EDD
    { wch: 15 }, // Tat Status
    { wch: 30 }, // Remarks
    { wch: 20 }, // E-way Bill Number
  ];
  worksheet["!cols"] = columnWidths;

  // Style the header row
  const headerRange = XLSX.utils.decode_range(worksheet["!ref"]!);
  for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
    const address = XLSX.utils.encode_cell({ r: headerRange.s.r, c: C });
    if (!worksheet[address]) continue;
    worksheet[address].s = {
      fill: { fgColor: { rgb: "FFFF00" } }, // Yellow background
      font: { bold: true }, // Bold text
      alignment: { horizontal: "center" }, // Center align
    };
  }

  XLSX.utils.book_append_sheet(workbook, worksheet, "Shipments");
  XLSX.writeFile(workbook, fileName);
}

export function daysPassed(sinceDate: string | undefined) {
  if (!sinceDate) return "";

  const givenDate = new Date(sinceDate);
  const currentDate = new Date();

  const diffTime = currentDate.getTime() - givenDate.getTime();

  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
}

export function formatTime(isoString: string): string {
  const date = new Date(isoString);

  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";

  // Convert 24hr format to 12hr format
  hours = hours % 12;
  // Convert 0 to 12 for midnight
  hours = hours ? hours : 12;

  // Add leading zero to minutes if needed
  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

  return `${hours}:${formattedMinutes} ${ampm}`;
}

export function formatTo24HourTime(dateInput: Date | string) {
  const date = new Date(dateInput);

  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");

  return `${hours}:${minutes}`;
}

type HandlerFunction = (req: NextRequest) => Promise<NextResponse>;

export function withErrorHandling(handler: HandlerFunction, dbClient: any) {
  return async (req: NextRequest) => {
    try {
      return await handler(req);
    } catch (err: any) {
      console.error(err);
      return NextResponse.json(
        { status: "error", message: err.message || "Internal server error" },
        { status: 500 }
      );
    } finally {
      await dbClient?.close();
    }
  };
}

// New function to check if user is from ABC Transport (super admin)
// export function isSuperAdmin(user: any) {
//   return user && (user.companyName === 'ABC Transport' || (user.data && user.data.companyName === 'ABC Transport'));
// }

export let INITIAL_SUPER_ADMINS: string[] = [
  "7506782597",
  "9820040124",
  "7776055139",
  "9867494961",
  "9892924121",
  "9137300966", // Ensuring this number is included
  "7045300242", // Ensuring this number is included
];

export function getSuperAdmins(): string[] {
  return INITIAL_SUPER_ADMINS;
}

export function addSuperAdmin(phoneNumber: string): void {
  if (!INITIAL_SUPER_ADMINS.includes(phoneNumber)) {
    INITIAL_SUPER_ADMINS.push(phoneNumber);
  }
}

export function removeSuperAdmin(phoneNumber: string): void {
  INITIAL_SUPER_ADMINS = INITIAL_SUPER_ADMINS.filter(
    (num) => num !== phoneNumber
  );
}

export async function isAdmin(user: any) {
  if (!user) return false;

  const userPhone = user?.phoneNumber?.toString();
  console.log("Checking admin status:", {
    userPhone,
    INITIAL_SUPER_ADMINS,
    role: user?.role,
  });

  // First check if user is a super admin
  if (userPhone && INITIAL_SUPER_ADMINS.includes(userPhone)) {
    return true;
  }

  // Then check if user has superAdmin role
  if (user.role === "superAdmin") {
    return true;
  }

  // Finally check other admin conditions
  return (
    user.role === "admin" ||
    (user.data &&
      (user.data.role === "admin" || user.data.role === "superAdmin"))
  );
}

export function isSubAdmin(user: any) {
  return user && user.role === "subAdmin";
}

export function isUser(user: any) {
  return user && user.role === "user";
}

export function isEmployee(user: any) {
  return user && user.role === "employee";
}

export function getInitials(name: string | undefined) {
  return name
    ?.split(" ")
    ?.map((word) => word.charAt(0).toUpperCase())
    .join("");
}
